// Widget-Loader. Wird als IIFE gebündelt und vom WordPress-Plugin als
// <script src="/widget.js"> in die Host-Seite eingehängt.
//
// Implementierungsentscheidung (2026-06-08): Das Widget rendert NICHT
// mehr eine eigene React-Variante des Chats. Stattdessen lädt es den
// Chat der Subdomain (Embed-Route `/embed`) in einem <iframe>. Damit
// gibt es nur noch eine Chat-Implementierung (die der Startseite); das
// Auto-Scroll-Verhalten ist konsistent, und das frühere
// scrollIntoView-Problem (Host-Seite scrollte beim Reload ans Widget-
// Ende) ist strukturell ausgeschlossen, weil das iframe ein eigenes
// Document-Scope hat.
//
// Konfiguration via data-Attribute auf einem <div class="cc-widget">:
//   data-api-base   — Origin der Subdomain (Default: prod-Subdomain)
//   data-mode       — "bubble" (Default) oder "inline"
//   data-embed-path — Pfad zum Embed (Default: "/embed")

const DEFAULT_API_BASE = "https://chat.forum-rathenau.de";
const DEFAULT_EMBED_PATH = "/embed";
const CLOSE_MESSAGE = "carbon-chat:close";
const SCROLL_MESSAGE = "carbon-chat:scroll";

// Scroll-Bridge (D1): Im Inline-Embed kann der Chat im iframe am Listenende
// nicht über die iframe-Grenze hinaus an die Host-Seite weiterscrollen. Der
// Chat postet dann den Rest-Scroll-Delta hierher; wir scrollen das
// Host-Fenster, damit man auf Mobil nicht im Chat „gefangen" ist. Ein
// einziger Listener für alle Inline-Widgets, gegen ihre Origins abgesichert.
const scrollOrigins = new Set<string>();
let scrollBridgeReady = false;
function enableScrollBridge(apiBase: string) {
  try {
    scrollOrigins.add(new URL(apiBase).origin);
  } catch {
    return;
  }
  if (scrollBridgeReady) return;
  scrollBridgeReady = true;
  window.addEventListener("message", (ev) => {
    const data = ev.data as { type?: string; dy?: number } | null;
    if (!data || data.type !== SCROLL_MESSAGE || !scrollOrigins.has(ev.origin)) return;
    const dy = Number(data.dy);
    if (Number.isFinite(dy) && dy !== 0) window.scrollBy(0, dy);
  });
}

// Style-Block für Bubble + iframe-Panel + Close-Button. Wird einmalig
// in den <head> injiziert. Klassen sind cc-* präfigiert, um Kollisionen
// mit Host-CSS zu minimieren; trotzdem keine Shadow-DOM-Isolation —
// die UI-Komplexität (Tailwind, Fonts, etc.) lebt im iframe.
const STYLE = `
.cc-widget-loader-bubble {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 58px;
  height: 58px;
  border-radius: 0;
  background: var(--cc-primary, #8DAE91);
  color: #ffffff;
  border: none;
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: 0 4px 16px rgba(35, 31, 32, 0.10);
  z-index: 2147483646;
  transition: transform 220ms cubic-bezier(.2,.6,.2,1), background 220ms cubic-bezier(.2,.6,.2,1);
}
.cc-widget-loader-bubble:hover {
  transform: scale(1.06);
  background: var(--cc-primary-strong, #6E9173);
}
.cc-widget-loader-bubble svg { width: 26px; height: 26px; }
.cc-widget-loader-panel {
  position: fixed;
  right: 24px;
  bottom: 24px;
  width: 400px;
  height: 640px;
  max-width: calc(100vw - 32px);
  max-height: calc(100vh - 32px);
  background: #ffffff;
  box-shadow: 0 12px 40px rgba(35, 31, 32, 0.14);
  z-index: 2147483647;
  display: none;
  overflow: hidden;
}
.cc-widget-loader-panel.is-open { display: block; }
.cc-widget-loader-panel iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
.cc-widget-loader-close {
  /* all:unset neutralisiert Host-Theme-Button-CSS (WordPress-Themes setzen
     padding/border-radius/min-height/font auf button) — danach wird der
     Look deterministisch wieder aufgebaut. Bündig in der Ecke als kantiges
     44px-Quadrat (Touch-Target-Standard); die /embed-Seite hält die rechte
     obere Header-Ecke dafür frei (BrandHeader compact, pr-12). */
  all: unset;
  box-sizing: border-box;
  position: absolute;
  top: 0;
  right: 0;
  width: 44px;
  height: 44px;
  background: var(--cc-primary, #8DAE91);
  color: #ffffff;
  cursor: pointer;
  z-index: 1;
  display: grid;
  place-items: center;
  border-radius: 0;
  transition: background 220ms cubic-bezier(.2,.6,.2,1);
}
.cc-widget-loader-close:hover { background: var(--cc-primary-strong, #6E9173); }
.cc-widget-loader-close:focus-visible { outline: 2px solid #231F20; outline-offset: -2px; }
.cc-widget-loader-close svg { width: 18px; height: 18px; }
.cc-widget-loader-inline {
  width: 100%;
  /* Erbt die vom Host gesetzte Höhe (Shortcode setzt style="height:..px").
     Falls der Host keine Höhe vorgibt, greift der min-height-Fallback. */
  height: 100%;
  min-height: 480px;
  background: #ffffff;
  overflow: hidden;
}
.cc-widget-loader-inline iframe {
  width: 100%;
  height: 100%;
  border: 0;
  display: block;
}
@media (max-width: 640px) {
  .cc-widget-loader-panel {
    right: 0;
    bottom: 0;
    width: 100vw;
    /* dvh statt vh: 100vh ist auf Mobile höher als der sichtbare Viewport
       (dynamische URL-Leiste) — das erzeugte in Safari die Lücke unter dem
       Panel. dvh-Fallback auf vh für alte Browser. Bei offener Tastatur
       übersteuert syncPanelToViewport() die Höhe per Inline-Style. */
    height: 100vh;
    height: 100dvh;
    max-width: 100vw;
    max-height: 100vh;
    max-height: 100dvh;
    overscroll-behavior: none;
  }
}
`;

function injectStyleOnce() {
  if (document.getElementById("cc-widget-loader-style")) return;
  const el = document.createElement("style");
  el.id = "cc-widget-loader-style";
  el.textContent = STYLE;
  document.head.appendChild(el);
}

function buildEmbedUrl(apiBase: string, embedPath: string, variant?: "inline"): string {
  // Seiten-Kontext an den Chat übergeben: URL + Titel der Host-Seite
  // als Query-Parameter (Kontrakt mit der /embed-Route). Werte werden
  // VOR dem Encoden gekappt, damit die iframe-URL nicht explodiert.
  // Im Bubble-Modus wird die src erst beim ersten Öffnen gesetzt —
  // location.href ist dann weiterhin die Host-Seite.
  // `variant=inline` schaltet in der /embed-Route natives Scroll-Chaining
  // frei (Shortcode scrollt in die Host-Seite weiter; Bubble bleibt
  // gekapselt).
  const pageUrl = window.location.href.slice(0, 500);
  const pageTitle = document.title.slice(0, 300);
  const parts: string[] = [];
  if (variant) parts.push(`variant=${variant}`);
  if (pageUrl) parts.push(`pageUrl=${encodeURIComponent(pageUrl)}`);
  if (pageTitle) parts.push(`pageTitle=${encodeURIComponent(pageTitle)}`);
  const base = `${apiBase.replace(/\/$/, "")}${embedPath}`;
  if (parts.length === 0) return base;
  return `${base}${base.includes("?") ? "&" : "?"}${parts.join("&")}`;
}

// Bildschirmfüllendes Panel (Mobile-Breakpoint des Style-Blocks oben).
const FULLSCREEN_QUERY = "(max-width: 640px)";

// Scroll-Lock der Host-Seite, solange das Panel bildschirmfüllend offen
// ist. `overflow:hidden` allein reicht auf iOS nicht (Body scrollt beim
// Touch-Rubber-Banding trotzdem) — deshalb die position:fixed-Technik
// mit gemerkter Scroll-Position.
let scrollLockY = 0;
let scrollLocked = false;
function lockHostScroll() {
  if (scrollLocked) return;
  scrollLocked = true;
  scrollLockY = window.scrollY;
  const b = document.body;
  b.style.position = "fixed";
  b.style.top = `-${scrollLockY}px`;
  b.style.left = "0";
  b.style.right = "0";
  b.style.width = "100%";
  b.style.overflow = "hidden";
}
function unlockHostScroll() {
  if (!scrollLocked) return;
  scrollLocked = false;
  const b = document.body;
  b.style.position = "";
  b.style.top = "";
  b.style.left = "";
  b.style.right = "";
  b.style.width = "";
  b.style.overflow = "";
  window.scrollTo(0, scrollLockY);
}

function mountBubble(host: HTMLElement, apiBase: string, embedPath: string) {
  // Panel + iframe vorbereiten, aber iframe-src erst beim ersten
  // Öffnen setzen — spart Ladezeit auf Seiten, auf denen der Chat nie
  // geöffnet wird, und verhindert dass das iframe beim Page-Load
  // bereits Token-Streaming startet.
  const panel = document.createElement("div");
  panel.className = "cc-widget-loader-panel";
  const iframe = document.createElement("iframe");
  iframe.title = "CarbonChat";
  iframe.allow = "clipboard-write";
  const closeBtn = document.createElement("button");
  closeBtn.type = "button";
  closeBtn.className = "cc-widget-loader-close";
  closeBtn.setAttribute("aria-label", "Schließen");
  // Geometrie-kritische Properties zusätzlich inline mit !important:
  // all:unset im Style-Block verliert gegen Theme-Regeln wie
  // `button { padding:… !important }` — Inline-!important gewinnt immer.
  closeBtn.style.cssText =
    "padding:0 !important; border-radius:0 !important; border:none !important; " +
    "min-width:0 !important; min-height:0 !important; width:44px !important; height:44px !important;";
  // SVG statt Text-Glyph: das ✕-Zeichen erbt sonst die Host-Schrift und
  // sitzt je nach Font schief in der Zeilenbox (gleiche Icon-Sprache wie
  // das Bubble-SVG unten).
  closeBtn.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18M6 6l12 12"/></svg>';
  panel.appendChild(closeBtn);
  panel.appendChild(iframe);

  const bubble = document.createElement("button");
  bubble.type = "button";
  bubble.className = "cc-widget-loader-bubble";
  bubble.setAttribute("aria-label", "CarbonChat öffnen");
  bubble.innerHTML =
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>';

  host.appendChild(panel);
  host.appendChild(bubble);

  const fullscreenMedia = window.matchMedia(FULLSCREEN_QUERY);

  // Tastatur-Handling: Öffnet sich die Bildschirmtastatur, schrumpft nur
  // der VISUELLE Viewport — 100dvh bleibt unverändert, das Eingabefeld
  // läge unter der Tastatur (DuckDuckGo/Safari-Bug). Deshalb wird die
  // Panel-Höhe bei offenem Fullscreen-Panel aktiv an visualViewport
  // gekoppelt; oben verankert, weil offsetTop bei eingeblendeter
  // Tastatur die sichtbare Oberkante beschreibt.
  function syncPanelToViewport() {
    const vv = window.visualViewport;
    if (!vv || !fullscreenMedia.matches || !panel.classList.contains("is-open")) {
      panel.style.top = "";
      panel.style.bottom = "";
      panel.style.height = "";
      return;
    }
    panel.style.top = `${vv.offsetTop}px`;
    panel.style.bottom = "auto";
    panel.style.height = `${vv.height}px`;
  }

  let loaded = false;
  function open() {
    if (!loaded) {
      iframe.src = buildEmbedUrl(apiBase, embedPath);
      loaded = true;
    }
    panel.classList.add("is-open");
    bubble.style.display = "none";
    if (fullscreenMedia.matches) lockHostScroll();
    syncPanelToViewport();
  }
  function close() {
    panel.classList.remove("is-open");
    bubble.style.display = "";
    unlockHostScroll();
    syncPanelToViewport();
  }

  bubble.addEventListener("click", open);
  closeBtn.addEventListener("click", close);

  // Viewport-Änderungen (Tastatur ein/aus, URL-Leiste, Rotation) und
  // Breakpoint-Wechsel (z. B. Rotation über die 640px-Grenze) nachziehen.
  window.visualViewport?.addEventListener("resize", syncPanelToViewport);
  window.visualViewport?.addEventListener("scroll", syncPanelToViewport);
  fullscreenMedia.addEventListener("change", () => {
    if (panel.classList.contains("is-open")) {
      if (fullscreenMedia.matches) lockHostScroll();
      else unlockHostScroll();
    }
    syncPanelToViewport();
  });

  // postMessage-Listener: das iframe kann ein cc-close-Event schicken
  // (z. B. wenn man später einen Close-Button INSIDE-iframe ergänzt).
  // origin-check, damit fremde Frames nicht ungewollt schließen.
  window.addEventListener("message", (ev) => {
    if (ev.data !== CLOSE_MESSAGE) return;
    try {
      const allowed = new URL(apiBase).origin;
      if (ev.origin === allowed) close();
    } catch {
      // ungültiges apiBase → ignorieren
    }
  });

  // URL-Trigger ?chat=open auf der Host-Seite öffnet den Chat sofort.
  if (window.location.search.includes("chat=open")) open();
}

function mountInline(host: HTMLElement, apiBase: string, embedPath: string) {
  const wrap = document.createElement("div");
  wrap.className = "cc-widget-loader-inline";
  const iframe = document.createElement("iframe");
  iframe.title = "CarbonChat";
  iframe.allow = "clipboard-write";
  iframe.src = buildEmbedUrl(apiBase, embedPath, "inline");
  wrap.appendChild(iframe);
  host.appendChild(wrap);

  // Scroll-Bridge nur noch als Rückwärtskompatibilität: seit
  // `variant=inline` nutzt die /embed-Route natives Scroll-Chaining
  // (overscroll-auto) und postet keine Scroll-Messages mehr. Ein älterer
  // /embed-Stand (Cache) postet weiterhin — dann greift die Bridge.
  enableScrollBridge(apiBase);
}

function mount(container: HTMLElement) {
  const apiBase = container.dataset.apiBase ?? DEFAULT_API_BASE;
  const embedPath = container.dataset.embedPath ?? DEFAULT_EMBED_PATH;
  const mode = (container.dataset.mode ?? "bubble") as "bubble" | "inline";

  injectStyleOnce();

  if (mode === "inline") {
    mountInline(container, apiBase, embedPath);
  } else {
    mountBubble(container, apiBase, embedPath);
  }
}

function initAll() {
  const containers = document.querySelectorAll<HTMLElement>(".cc-widget");
  if (containers.length > 0) {
    containers.forEach(mount);
    return;
  }
  // Kein expliziter Container → Floating-Bubble automatisch erzeugen
  const auto = document.createElement("div");
  auto.className = "cc-widget";
  document.body.appendChild(auto);
  mount(auto);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initAll);
} else {
  initAll();
}
